import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { certificationKeys } from "@/lib/query";
import type {
  CertificationCreateInput,
  CertificationStatus,
  CertificationUpdateInput,
} from "@/schemas/certifications";

export interface Certification {
  id: string;
  name: string;
  issuingBody: string | null;
  description: string | null;
  status: CertificationStatus;
  createdAt: string;
  updatedAt: string;
}

interface CertificationsListResponse {
  data: Certification[];
  nextCursor: string | null;
  prevCursor: string | null;
  hasNext: boolean;
  hasPrev: boolean;
  total: number;
}

interface PageRequest {
  cursor: string | null;
  direction: "next" | "prev";
}

interface Filters {
  search: string;
  statuses: string[];
  sort?: string;
  dir?: "asc" | "desc";
}

export function useCertifications(filters: Filters, pageRequest: PageRequest) {
  return useQuery({
    queryKey: [...certificationKeys.list(filters), pageRequest] as const,
    queryFn: async (): Promise<CertificationsListResponse> => {
      const params = new URLSearchParams();
      params.set("limit", "50");
      if (pageRequest.cursor) {
        params.set("cursor", pageRequest.cursor);
        params.set("direction", pageRequest.direction);
      }
      if (filters.search) params.set("search", filters.search);
      if (filters.statuses.length) params.set("status", filters.statuses.join(","));
      if (filters.sort) {
        params.set("sort", filters.sort);
        params.set("dir", filters.dir ?? "asc");
      }
      const res = await fetch(`/api/certifications?${params}`);
      if (!res.ok) throw new Error("Failed to fetch certifications");
      return res.json();
    },
  });
}

export function useCertification(id: string) {
  return useQuery({
    queryKey: certificationKeys.detail(id),
    queryFn: async (): Promise<Certification | null> => {
      const res = await fetch(`/api/certifications/${id}`);
      if (res.status === 404) return null;
      if (!res.ok) throw new Error("Failed to fetch certification");
      const json = await res.json();
      return json.data;
    },
  });
}

export interface CertificationPersonRow {
  id: string;
  personId: string;
  personName: string;
  personTitle: string | null;
  status: "assigned" | "started" | "completed";
  assignedAt: string;
  startedAt: string | null;
  completedAt: string | null;
  expiresAt: string | null;
}

export function useCertificationPeople(id: string) {
  return useQuery({
    queryKey: certificationKeys.people(id),
    queryFn: async (): Promise<CertificationPersonRow[]> => {
      const res = await fetch(`/api/certifications/${id}/people`);
      if (!res.ok) throw new Error("Failed to fetch certification holders");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useCreateCertification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CertificationCreateInput) => {
      const res = await fetch("/api/certifications", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error("Create failed");
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: certificationKeys.all });
      toast.success("Certification created");
    },
    onError: () => toast.error("Failed to create certification"),
  });
}

export function useUpdateCertification(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CertificationUpdateInput) => {
      const res = await fetch(`/api/certifications/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error("Update failed");
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: certificationKeys.all });
      toast.success("Certification updated");
    },
    onError: () => toast.error("Failed to update certification"),
  });
}
